import React, { useState, useEffect } from 'react'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import SeveranceNotice from './SeveranceNotice'
import { severanceWarning, countListings, type PropertyGroupRow } from '@/lib/groups'
import { apiFetch, apiErrorMessage } from '@/lib/csrf'
import { AlertCircle, Loader2, Trash2 } from 'lucide-react'
import type { DecoupleResult } from './DecoupleMemberDialog'

interface DissolveGroupDialogProps {
  /**
   * `/a/:account_slug/settings` — the business's settings root, served by the backend
   * (`currentAccount.settingsPath`). Every endpoint below is this plus a FIXED suffix.
   */
  settingsPath: string
  group: PropertyGroupRow | null
  onClose: () => void
  onDone: (result: DecoupleResult) => void
}

// SPLITTING UP A COMBINED INVENTORY ("dissolve" in code): every listing leaves at once.
//
// Same warning logic as removing one hotel, because it is the same act performed N times: every
// cross-property parent/child link in the group is severed, and every severed child becomes
// independent (locked product rule 4). The group's whole severance set is previewed server-side by
// the dissolve mutation itself, so this dialog cannot understate what a single decouple would have
// warned about.
//
// A combined inventory is called by its lead listing's name (`group.name` is the lead's), so the title
// says "the combined inventory led by X" rather than "Split up X?", which reads as splitting a listing.
// The typed confirmation is that name.
//
// The typed confirmation is required on the same condition as a single decouple: when rooms will
// actually be left without a price. Dissolving a group whose rooms are all independent already is a
// reversible act with no pricing consequence at all, and asking for a ritual there is what trains
// operators to type past the ones that matter.
export default function DissolveGroupDialog({ settingsPath, group, onClose, onDone }: DissolveGroupDialogProps) {
  const [confirmation, setConfirmation] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setConfirmation('')
    setError(null)
    setSubmitting(false)
  }, [group?.id])

  const severed = group?.dissolvePreview?.severed ?? []
  const warning = severanceWarning(severed)
  const requiresTypedName = warning.tone === 'warning'
  const nameMatches = group ? confirmation.trim().toLowerCase() === group.name.toLowerCase() : false
  const canConfirm = !!group && !submitting && (!requiresTypedName || nameMatches)
  const memberNames = (group?.members ?? []).map((member) => member.name)

  async function dissolve() {
    if (!group) return
    setError(null)
    setSubmitting(true)

    try {
      const response = await apiFetch(`${settingsPath}/groups/${group.id}`, { method: 'DELETE' })
      const data = await response.json()

      if (!response.ok) {
        setError(data.errors?.join(', ') || 'This combined inventory could not be split up. Try again.')
        return
      }

      onDone({ headline: data.headline, severed: data.severed ?? [], subject: 'each listing' })
    } catch (e) {
      setError(apiErrorMessage(e, 'Network error. Please check your connection and try again.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AlertDialog open={group !== null} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent className="sm:max-w-[540px]">
        <AlertDialogHeader>
          <AlertDialogTitle>Split up the combined inventory led by {group?.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            All {countListings(group?.hotelCount ?? 0)} go back to pricing on their own, each from its own saved base
            rates, seasons, guardrails and targets, kept untouched while {group?.leadPropertyName} led. Nothing is
            deleted, and you can set it up again at any time.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogBody className="space-y-4">
          <div className="rounded-lg border border-border/60 bg-muted/40 px-3.5 py-2.5">
            <p className="text-xs font-medium text-foreground">Going back to pricing on their own</p>
            <p className="mt-1 text-xs text-muted-foreground">{memberNames.join(' · ')}</p>
          </div>

          <SeveranceNotice severed={severed} mode="before" subject="each listing" />

          {requiresTypedName && (
            <div className="space-y-2">
              <Label htmlFor="dissolve-confirm" className="text-xs">
                Type <span className="font-semibold text-foreground">{group?.name}</span> to confirm
              </Label>
              <Input
                id="dissolve-confirm"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                placeholder={group?.name}
                autoComplete="off"
              />
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </AlertDialogBody>

        <AlertDialogFooter>
          <Button type="button" variant="ghost" className="rounded-lg" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="rounded-lg"
            onClick={dissolve}
            disabled={!canConfirm}
            aria-busy={submitting}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Splitting up…
              </>
            ) : (
              <>
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Split up
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
